import {
  GAMIFICATION_VALIDITY_DAYS_MAX,
  type GamificationConfigApi,
} from '../gamificationConfigTypes';
import { useCallback, useEffect, useState } from 'react';

/** Quando a API devolve `pointsValidityDays: null`, a UI usa este valor editável (alinhado ao mock). */
export const GAMIFICATION_DEFAULT_DAYS_WHEN_NULL = 365;

function clampValidityDays(n: number): number {
  return Math.min(GAMIFICATION_VALIDITY_DAYS_MAX, Math.max(0, n));
}

export function useGamificationConfig(gamificationApi: GamificationConfigApi) {
  const [pointsValidityDays, setPointsValidityDays] = useState<number>(
    GAMIFICATION_DEFAULT_DAYS_WHEN_NULL
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await gamificationApi.fetchGamificationConfig();
      const raw = cfg.pointsValidityDays;
      setPointsValidityDays(
        raw === null || raw === undefined
          ? GAMIFICATION_DEFAULT_DAYS_WHEN_NULL
          : clampValidityDays(raw)
      );
    } catch {
      setError('load_error');
    } finally {
      setLoading(false);
    }
  }, [gamificationApi]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await gamificationApi.updateGamificationConfig({
        pointsValidityDays: clampValidityDays(pointsValidityDays),
      });
    } catch {
      setError('save_error');
    } finally {
      setSaving(false);
    }
  }, [gamificationApi, pointsValidityDays]);

  return {
    pointsValidityDays,
    setPointsValidityDays,
    loading,
    saving,
    error,
    reload: load,
    save,
  };
}
